import { cx } from "@shared/components/untitledui/util/cx"
import type { Transition } from "motion/react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import type { ReactNode } from "react"
import { Radio, RadioGroup } from "react-aria-components"
import { choiceCardShellClassName } from "./choice-card-chrome"
import type { ChoiceCardFaceProps } from "./ChoiceCardFace"
import { ChoiceCardFace } from "./ChoiceCardFace"

const REVEAL_DURATION_S = 0.25

/** ember-animated's default easing (easings/cosine.ts), as the drilldown swap uses. */
const easeInAndOut = (t: number) => 0.5 - Math.cos(t * Math.PI) / 2

export interface ChoiceCardOption<Value extends string> extends ChoiceCardFaceProps {
  value: Value
  /** Revealed inside this card while it is the picked one — its links, its actions. */
  detail?: ReactNode
}

/**
 * Radio buttons drawn as selectable cards (per the Figma menus). The view's Done button acts
 * on the selection; for cards that act the moment they are pressed, use {@link
 * import("./ChoiceButtons").ChoiceButtons}.
 */
export function ChoiceCards<Value extends string>({
  ariaLabel,
  options,
  value,
  onChange,
  columns,
}: {
  ariaLabel: string
  options: Array<ChoiceCardOption<Value>>
  value: Value | null
  onChange: (value: Value) => void
  /** Lay the cards out side by side instead of stacked. */
  columns?: boolean
}) {
  return (
    <RadioGroup
      aria-label={ariaLabel}
      value={value}
      // Values can only come from `options`, so the widening to string is safe to undo.
      onChange={(newValue) => onChange(newValue as Value)}
      className={cx("flex gap-2.5", columns ? "items-stretch" : "flex-col")}
    >
      {options.map(({ value: optionValue, detail, ...face }) => {
        const isSelected = optionValue === value
        return (
          // The card is a shell around the Radio rather than the Radio itself: the Radio
          // renders a <label>, whose content model forbids the buttons and links a detail
          // carries (and whose accessible name would absorb the detail's text). The detail
          // sits beside the label, inside the shell, so it still reads as one card.
          <span
            key={optionValue}
            className={cx("flex flex-col", choiceCardShellClassName(isSelected))}
          >
            <Radio
              value={optionValue}
              data-test="choice card"
              className="block grow cursor-pointer p-4 outline-hidden"
            >
              <ChoiceCardFace {...face} />
            </Radio>
            {detail === undefined ? null : (
              <CardDetail isOpen={isSelected}>
                <span className="block px-4 pb-4">{detail}</span>
              </CardDetail>
            )}
          </span>
        )
      })}
    </RadioGroup>
  )
}

/**
 * A card's detail, opening and closing with it. Both cards animate at once — the one letting
 * go collapses while the picked one grows — so the stack never jumps to its new size.
 */
function CardDetail({ isOpen, children }: { isOpen: boolean; children: ReactNode }) {
  const reduceMotion = useReducedMotion()
  const transition: Transition = {
    duration: reduceMotion ? 0 : REVEAL_DURATION_S,
    ease: easeInAndOut,
  }

  return (
    // Outside the conditional: an AnimatePresence that unmounts with its child can't animate
    // the exit.
    <AnimatePresence initial={false}>
      {isOpen ? (
        // marginTop animates with the height: fully open, the detail tucks 12px into the
        // label's bottom padding (the Figma 4px face-to-detail gap). It has to ride on this
        // span, outside the overflow-hidden box — inside, the clip swallows the detail's top
        // — and it has to animate, or the tuck would dent the card while the height is 0.
        <motion.span
          key="detail"
          className="block overflow-hidden"
          initial={{ height: 0, opacity: 0, marginTop: 0 }}
          animate={{ height: "auto", opacity: 1, marginTop: -12 }}
          exit={{ height: 0, opacity: 0, marginTop: 0 }}
          transition={transition}
        >
          {children}
        </motion.span>
      ) : null}
    </AnimatePresence>
  )
}
